import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Landing page configuration constants
const LANDING_IMAGE_TILT_DEG = 15;
const LANDING_IMAGE_CENTER_SCALE = 0.97;


interface PipelineSection {
  id: string;
  route: string;
  title: string;
  description: string;
  images: string[];
  imageStyle: 'tilted' | 'flat';
}

const pipelines: PipelineSection[] = [
  {
    id: 'christmas-handtowel',
    route: '/christmas-handtowel/creator',
    title: 'Christmas Hand Towel',
    description: 'Create christmas watercolor style artwork from your dog\'s photo — printed on a hand towel and delivered home.',
    images: [
      '/christmas-handtowel-landing/1.png',
      '/christmas-handtowel-landing/2.png',
      '/christmas-handtowel-landing/3.png'
    ],
    imageStyle: 'flat'
  },
  {
    id: 'animated-mug',
    route: '/animated-mug/creator',
    title: 'Disney Mug',
    description: 'Create a disney style portrait from your dog\'s photo — printed on a mug and delivered home.',
    images: [
      '/animated-mug-landing/1.png',
      '/animated-mug-landing/2.png',
      '/animated-mug-landing/3.png'
    ],
    imageStyle: 'flat'
  },
  {
    id: 'portrait-mug',
    route: '/portrait-mug/creator',
    title: 'Portrait Mug',
    description: 'Create a portrait from your dog\'s photo — printed on a mug and delivered home.',
    images: [
      '/portrait-mug-landing/1.png',
      '/portrait-mug-landing/2.png',
      '/portrait-mug-landing/3.png'
    ],
    imageStyle: 'flat'
  },
  {
    id: 'bathtub',
    route: '/bathtub/creator',
    title: 'Bathtime Canvas Print',
    description: 'Create a bathroom centerpiece from your dog\'s photo — printed on canvas and delivered home.',
    images: [
      '/bathtub-landing/1.png',
      '/bathtub-landing/2.png',
      '/bathtub-landing/3.png'
    ],
    imageStyle: 'tilted'
  },
  {
    id: 'bathtub-curtain',
    route: '/bathtub-curtain/creator',
    title: 'Bathtime Shower Curtain',
    description: 'Create a shower curtain from your dog\'s photo — printed on a shower curtain and delivered home.',
    images: [
      '/bathtub-curtain-landing/1.png',
      '/bathtub-curtain-landing/2.png',
      '/bathtub-curtain-landing/3.png'
    ],
    imageStyle: 'tilted'
  }
];



function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9fafa' }}>
      <div className="container mx-auto px-2 sm:px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-12">
            <img
              src="/logo.png"
              alt="Onelife Creations"
              className="h-16 sm:h-20 md:h-24 w-auto mx-2"
            />
          </div>


          {/* All pipelines in a single card */}
          <div className="rounded-2xl bg-white px-6 py-6 text-center shadow-xl sm:px-10">
            {/* How it works section inside main card */}
            <div className="mb-4">
              {/* Desktop layout */}
              <div className="hidden md:flex items-stretch justify-between gap-0 mt-0 mb-0">
                {/* Step 1 */}
                <div>
                  <div className="rounded-xl bg-white px-2 md:px-2 lg:px-2 py-2 md:py-2 lg:py-2  flex flex-row h-full">
                    <div className="flex-shrink-0 mr-6 self-center">
                      <img
                        src="/how-it-works-section/icon_1.svg"
                        alt="Step 1"
                        className="w-10 h-10 md:w-10 md:h-10 lg:w-10 lg:h-10 xl:w-10 xl:h-10"
                      />
                    </div>
                    <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                      <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Upload a photo</h3>
                      <p className="text-xs text-slate-600 leading-relaxed mt-1">
                        We'll turn it into stunning art in minutes
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 w-4 md:w-6 lg:w-8 flex items-center justify-center">
                  <div className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-gray-400"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Step 2 */}
                <div>
                  <div className="rounded-xl bg-white px-2 md:px-2 lg:px-2 py-2 md:py-2 lg:py-2 flex flex-row h-full">
                    <div className="flex-shrink-0 mr-6 self-center">
                      <img
                        src="/how-it-works-section/icon_2.svg"
                        alt="Step 2"
                        className="w-10 h-10 md:w-10 md:h-10 lg:w-10 lg:h-10 xl:w-10 xl:h-10"
                      />
                    </div>
                    <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                      <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Try before you buy</h3>
                      <p className="text-xs text-slate-600 leading-relaxed mt-1">
                        No payment or signup required
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 w-4 md:w-6 lg:w-8 flex items-center justify-center">
                  <div className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-gray-400"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Step 3 */}
                <div>
                  <div className="rounded-xl bg-white px-2 md:px-2 lg:px-2 py-2 md:py-2 lg:py-2  flex flex-row h-full">
                    <div className="flex-shrink-0 mr-6 self-center">
                      <img
                        src="/how-it-works-section/icon_3.svg"
                        alt="Step 3"
                        className="w-10 h-10 md:w-10 md:h-10 lg:w-10 lg:h-10 xl:w-10 xl:h-10"
                      />
                    </div>
                    <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                      <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Order & enjoy</h3>
                      <p className="text-xs text-slate-600 leading-relaxed mt-1">
                        We'll print and ship your art where it belongs
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile layout */}
              <div className="md:hidden flex justify-center">
                <div className="flex flex-col items-start">
                  {/* Step 1 */}
                  <div className="max-w-md">
                    <div className="rounded-xl bg-white px-4 py-5  flex flex-row items-start">
                      <div className="flex-shrink-0 mr-4 self-center">
                        <img
                          src="/how-it-works-section/icon_1.svg"
                          alt="Step 1"
                          className="w-8 h-8 sm:w-10 sm:h-12"
                        />
                      </div>
                      <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                        <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Upload a photo</h3>
                        <p className="text-xs text-slate-600 leading-relaxed mt-1">
                          We'll turn it into stunning art in minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="self-center pt-6 pb-5 w-6 h-6 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-gray-400"
                    >
                      <path
                        d="M19 9L12 15L5 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* Step 2 */}
                  <div className="max-w-md">
                    <div className="rounded-xl bg-white px-4 py-5  flex flex-row items-start">
                      <div className="flex-shrink-0 mr-4 self-center">
                        <img
                          src="/how-it-works-section/icon_2.svg"
                          alt="Step 2"
                          className="w-8 h-8 sm:w-10 sm:h-12"
                        />
                      </div>
                      <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                        <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Try before you buy</h3>
                        <p className="text-xs text-slate-600 leading-relaxed mt-1">
                          No payment or signup required
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="self-center pt-6 pb-5 w-6 h-6 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-gray-400"
                    >
                      <path
                        d="M19 9L12 15L5 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* Step 3 */}
                  <div className="max-w-md">
                    <div className="rounded-xl bg-white px-4 py-5  flex flex-row items-start">
                      <div className="flex-shrink-0 mr-4 self-center">
                        <img
                          src="/how-it-works-section/icon_3.svg"
                          alt="Step 3"
                          className="w-8 h-8 sm:w-10 sm:h-12"
                        />
                      </div>
                      <div className="flex-1 text-left flex flex-col justify-start items-start self-start">
                        <h3 className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">Order & enjoy</h3>
                        <p className="text-xs text-slate-600 leading-relaxed mt-1">
                          We'll print and ship your art where it belongs
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before/After Example Section */}
            <div className="mt-8 mb-8">
              <img
                src="/example-landing/example.png"
                alt="Example"
                className="w-full h-auto"
              />
            </div>

            <div className="border-t border-gray-200 pt-12"></div>

            {pipelines.map((pipeline, index) => (
              <div key={pipeline.id}>
                {/* Subtle divider between sections (not before first) */}
                {index > 0 && (
                  <div className="my-12 border-t border-gray-200"></div>
                )}

                <div className="space-y-3 text-left">
                  <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl md:text-[34px] md:leading-none whitespace-nowrap">
                    {pipeline.title}
                  </h1>
                  <p className="text-sm text-slate-600 sm:text-base">
                    {pipeline.description}
                  </p>
                </div>

                {/* Images grid */}
                {pipeline.imageStyle === 'tilted' ? (
                  <div className="mt-10 mb-16 grid grid-cols-3 gap-4 items-center">
                    <div className="relative">
                      <img
                        src={pipeline.images[0]}
                        alt="Example 1"
                        className={`w-full h-auto rounded-lg ${pipeline.id === 'bathtub' ? 'shadow-lg' : ''}`}
                        style={{
                          transform: `perspective(1000px) rotateY(${LANDING_IMAGE_TILT_DEG}deg)`,
                          boxShadow: pipeline.id === 'bathtub' ? '0 8px 16px rgba(0, 0, 0, 0.15)' : undefined
                        }}
                      />
                    </div>
                    <div className="relative flex items-center justify-center">
                      <img
                        src={pipeline.images[1]}
                        alt="Example 2"
                        className={`w-full rounded-lg ${pipeline.id === 'bathtub' ? 'shadow-lg' : ''}`}
                        style={{
                          transform: `scale(${LANDING_IMAGE_CENTER_SCALE})`,
                          boxShadow: pipeline.id === 'bathtub' ? '0 8px 16px rgba(0, 0, 0, 0.15)' : undefined
                        }}
                      />
                    </div>
                    <div className="relative">
                      <img
                        src={pipeline.images[2]}
                        alt="Example 3"
                        className={`w-full h-auto rounded-lg ${pipeline.id === 'bathtub' ? 'shadow-lg' : ''}`}
                        style={{
                          transform: `perspective(1000px) rotateY(-${LANDING_IMAGE_TILT_DEG}deg)`,
                          boxShadow: pipeline.id === 'bathtub' ? '0 8px 16px rgba(0, 0, 0, 0.15)' : undefined
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={`grid grid-cols-3 gap-0 items-center ${pipeline.id === 'christmas-handtowel' ? 'pt-12 mb-16' : 'mb-4'}`}>
                    <div className="relative overflow-hidden">
                      <img
                        src={pipeline.images[0]}
                        alt="Example 1"
                        className="w-full h-auto block"
                        style={pipeline.id === 'christmas-handtowel' ? {
                          maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)'
                        } : undefined}
                      />
                    </div>
                    <div className="relative overflow-hidden">
                      <img
                        src={pipeline.images[1]}
                        alt="Example 2"
                        className="w-full h-auto block"
                        style={pipeline.id === 'christmas-handtowel' ? {
                          maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)'
                        } : undefined}
                      />
                    </div>
                    <div className="relative overflow-hidden">
                      <img
                        src={pipeline.images[2]}
                        alt="Example 3"
                        className="w-full h-auto block"
                        style={pipeline.id === 'christmas-handtowel' ? {
                          maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)'
                        } : undefined}
                      />
                    </div>
                  </div>
                )}

                {/* Create button */}
                <div className="text-center">
                  <Link
                    to={pipeline.route}
                    className={`inline-flex items-center justify-center rounded-full px-12 py-1.5 text-sm font-semibold transition hover:opacity-90 border border-gray-300 ${index === pipelines.length - 1 ? 'mb-10' : 'mb-3'}`}
                    style={{ backgroundColor: '#EFBF04', boxShadow: '0 0 4px rgba(0, 0, 0, 0.15)', color: '#3A3A3A' }}
                  >
                    Create
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
